import { useState, useEffect, useRef, useCallback } from "react";
import "./Dashboard.css";
import puppyHungry from "../assets/images/puppy_hungry.gif";
import puppyFull   from "../assets/images/puppy_full.gif";
import puppyHappy  from "../assets/images/puppy_happy.gif";
import { apiFetch } from "../apiBase";

const MOOD_IMAGES: Record<"hungry" | "full" | "happy", string> = {
  hungry: puppyHungry,
  full:   puppyFull,
  happy:  puppyHappy,
};

const UNIT_TO_G: Record<string, number> = { g: 1, ml: 1, oz: 28.3495, cup: 240, tbsp: 15, tsp: 5 };
const STANDARD_UNITS = ["g", "ml", "oz", "cup", "tbsp", "tsp"];

// ─── Food suggestion type (mirrors RecipeBuilder) ─────────

interface FoodSuggestion {
  food_id:           string;
  description:       string;
  calories_per_100g: number | null;
  protein_per_100g:  number | null;
  fat_per_100g:      number | null;
  carbs_per_100g:    number | null;
}

const STANDARD_UNIT_SET = new Set(STANDARD_UNITS);

function toGrams(amount: string, unit: string, portionsMap: Record<string, number> = {}): number {
  const conv = { ...UNIT_TO_G, ...portionsMap };
  return (parseFloat(amount) || 0) * (conv[unit] ?? 1);
}

// ─── Types ────────────────────────────────────────────────

interface NutritionData {
  calories: {
    consumed: number;
    goal: number;
  };
  protein: {
    consumed: number;
    goal: number;
  };
  carbs: {
    consumed: number;
    goal: number;
  };
  fat: {
    consumed: number;
    goal: number;
  };
}

type MealCategory = "breakfast" | "lunch" | "dinner" | "snacks";

interface Meal {
  id: string;
  name: string;
  time: string;
  category: MealCategory;
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
}

// Proportions must sum to 1.0
const MEAL_PROPORTIONS: { key: MealCategory; label: string; pct: number }[] = [
  { key: "breakfast", label: "Breakfast", pct: 0.25 },
  { key: "lunch",     label: "Lunch",     pct: 0.30 },
  { key: "dinner",    label: "Dinner",    pct: 0.35 },
  { key: "snacks",    label: "Snacks",    pct: 0.10 },
];

interface Recipe {
  id: string;
  name: string;
  total_calories: number;
  total_protein_g: number;
  total_fat_g: number;
  total_carbs_g: number;
}

interface BodyMeasurement {
  id:           string;
  measured_at:  string;
  weight_kg:    number | null;
  height_cm:    number | null;
  waist_cm:     number | null;
  neck_cm:      number | null;
  hip_cm:       number | null;
  body_fat_pct: number | null;
}

interface DashboardProps {
  nutritionData: NutritionData;
  userProfile: any;
  onOpenRecipeBuilder: () => void;
  onOpenSettings: () => void;
  onOpenSharedRecipes: () => void;
  onOpenBulkImport: () => void;
  onOpenMealPlanner: () => void;
}

// ─── FoodLogInput component ───────────────────────────────

interface FoodLogInputProps {
  category: MealCategory;
  logDate:  string;
  onLogged: () => void;
}

function FoodLogInput({ category, logDate, onLogged }: FoodLogInputProps) {
  const [query, setQuery]             = useState("");
  const [suggestions, setSuggestions] = useState<FoodSuggestion[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selected, setSelected]       = useState<FoodSuggestion | null>(null);
  const [portionsMap, setPortionsMap] = useState<Record<string, number>>({});
  const [amount, setAmount]           = useState("100");
  const [unit, setUnit]               = useState("g");
  const [logging, setLogging]         = useState(false);
  const [error, setError]             = useState("");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapRef     = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node))
        setShowDropdown(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleQueryChange = (value: string) => {
    setQuery(value);
    setSelected(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (value.trim().length < 2) { setSuggestions([]); setShowDropdown(false); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await apiFetch(
          `/foods/search?query=${encodeURIComponent(value.trim())}&page_size=50`,
          { headers: { Authorization: `Bearer ${token ?? ""}` } }
        );
        if (!res.ok) return;
        const data = await res.json();
        const results: FoodSuggestion[] = (data.foods ?? []).map((f: any) => ({
          food_id:           f.food_id,
          description:        f.description,
          calories_per_100g: f.calories_per_100g ?? null,
          protein_per_100g:  f.protein_per_100g  ?? null,
          fat_per_100g:      f.fat_per_100g      ?? null,
          carbs_per_100g:    f.carbs_per_100g    ?? null,
        }));
        setSuggestions(results);
        setShowDropdown(results.length > 0);
      } catch { /* ignore */ }
    }, 350);
  };

  const selectFood = async (food: FoodSuggestion) => {
    setQuery(food.description);
    setSelected(food);
    setSuggestions([]);
    setShowDropdown(false);

    const token   = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token ?? ""}` };

    const fetchPortions = async (foodId: string): Promise<Record<string, number> | null> => {
      try {
        const res = await apiFetch(`/foods/${encodeURIComponent(foodId)}`, { headers });
        if (!res.ok) return null;
        const detail = await res.json();
        const map: Record<string, number> = {};
        for (const p of detail.portions ?? []) {
          if (p.unit && p.grams) map[p.unit] = p.grams;
        }
        return Object.keys(map).length > 0 ? map : null;
      } catch { return null; }
    };

    const portions = await fetchPortions(food.food_id);

    const pm = portions ?? {};
    setPortionsMap(pm);
    const natural = Object.keys(pm).filter(u => !STANDARD_UNIT_SET.has(u));
    if (natural.length > 0) { setUnit(natural[0]); setAmount("1"); }
    else { setUnit("g"); setAmount("100"); }
  };

  const handleLog = async () => {
    if (!selected) { setError("Select a food first."); return; }
    if (!amount.trim() || parseFloat(amount) <= 0) { setError("Enter a valid amount."); return; }
    setError("");
    setLogging(true);
    const amount_g = toGrams(amount, unit, portionsMap);
    const sc = (v: number | null) => v != null ? Math.round((v * amount_g / 100) * 10) / 10 : 0;
    const token = localStorage.getItem("token");
    try {
      const res = await apiFetch("/logs", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token ?? ""}` },
        body: JSON.stringify({
          food_id:   selected.food_id,
          food_name: selected.description,
          amount_g,
          calories:  sc(selected.calories_per_100g),
          protein_g: sc(selected.protein_per_100g),
          carbs_g:   sc(selected.carbs_per_100g),
          fat_g:     sc(selected.fat_per_100g),
          meal:      category,
          log_date:  logDate,
        }),
      });
      if (!res.ok) throw new Error();
      setQuery(""); setSelected(null); setAmount("100"); setUnit("g"); setPortionsMap({});
      onLogged();
    } catch {
      setError("Failed to log food.");
    } finally {
      setLogging(false);
    }
  };

  const allUnits = [
    ...Object.keys(portionsMap).filter(u => !STANDARD_UNIT_SET.has(u)),
    ...STANDARD_UNITS,
  ];

  return (
    <div className="food-log-input" ref={wrapRef}>
      <div className="food-search-wrap">
        <input
          type="text"
          placeholder="Search food…"
          value={query}
          onChange={e => handleQueryChange(e.target.value)}
          onFocus={() => suggestions.length > 0 && setShowDropdown(true)}
          autoComplete="off"
          className="food-search-input"
        />
        {showDropdown && (
          <ul className="food-autocomplete-dropdown">
            {suggestions.map(f => (
              <li
                key={f.food_id}
                onMouseDown={e => { e.preventDefault(); selectFood(f); }}
                className="food-autocomplete-item"
              >
                <span className="food-autocomplete-name">{f.description}</span>
                {f.calories_per_100g != null && (
                  <span className="food-autocomplete-kcal">
                    {Math.round(f.calories_per_100g)} kcal/100g
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
      {selected && (
        <div className="food-amount-row">
          <input
            type="number"
            min="0"
            step="any"
            value={amount}
            onChange={e => setAmount(e.target.value)}
            className="food-amount-input"
          />
          <select value={unit} onChange={e => setUnit(e.target.value)} className="food-unit-select">
            {allUnits.map(u => <option key={u} value={u}>{u}</option>)}
          </select>
          {selected.calories_per_100g != null && (
            <span className="food-kcal-preview">
              ≈ {Math.round(selected.calories_per_100g * toGrams(amount, unit, portionsMap) / 100)} kcal
            </span>
          )}
        </div>
      )}
      {error && <p className="food-log-error">{error}</p>}
      <button
        type="button"
        onClick={handleLog}
        disabled={!selected || logging}
        className="food-log-button"
      >
        {logging ? "Logging…" : "Log food"}
      </button>
    </div>
  );
}

// ─── CustomFoodInput component ────────────────────────────

interface CustomFoodInputProps {
  category: MealCategory;
  logDate:  string;
  onLogged: () => void;
}

function CustomFoodInput({ category, logDate, onLogged }: CustomFoodInputProps) {
  const [name,     setName]     = useState("");
  const [calories, setCalories] = useState("");
  const [protein,  setProtein]  = useState("");
  const [carbs,    setCarbs]    = useState("");
  const [fat,      setFat]      = useState("");
  const [logging,  setLogging]  = useState(false);
  const [error,    setError]    = useState("");

  const handleLog = async () => {
    if (!name.trim())                         { setError("Enter a food name."); return; }
    if (!calories || parseFloat(calories) < 0) { setError("Enter valid calories."); return; }
    setError("");
    setLogging(true);
    const token = localStorage.getItem("token");
    try {
      const res = await apiFetch("/logs", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token ?? ""}` },
        body: JSON.stringify({
          food_name: name.trim(),
          amount_g:  100,
          calories:  parseFloat(calories) || 0,
          protein_g: parseFloat(protein)  || 0,
          carbs_g:   parseFloat(carbs)    || 0,
          fat_g:     parseFloat(fat)      || 0,
          meal:      category,
          log_date:  logDate,
        }),
      });
      if (!res.ok) throw new Error();
      setName(""); setCalories(""); setProtein(""); setCarbs(""); setFat("");
      onLogged();
    } catch {
      setError("Failed to log food.");
    } finally {
      setLogging(false);
    }
  };

  return (
    <div className="custom-food-input">
      <input
        type="text"
        placeholder="Food name (e.g. Protein bar, Brand X)"
        value={name}
        onChange={e => setName(e.target.value)}
        className="custom-food-name"
      />
      <div className="custom-food-macros">
        <label className="custom-food-field">
          <span>Calories</span>
          <input type="number" min="0" step="any" placeholder="0"
            value={calories} onChange={e => setCalories(e.target.value)} />
        </label>
        <label className="custom-food-field">
          <span>Protein (g)</span>
          <input type="number" min="0" step="any" placeholder="0"
            value={protein} onChange={e => setProtein(e.target.value)} />
        </label>
        <label className="custom-food-field">
          <span>Carbs (g)</span>
          <input type="number" min="0" step="any" placeholder="0"
            value={carbs} onChange={e => setCarbs(e.target.value)} />
        </label>
        <label className="custom-food-field">
          <span>Fat (g)</span>
          <input type="number" min="0" step="any" placeholder="0"
            value={fat} onChange={e => setFat(e.target.value)} />
        </label>
      </div>
      {error && <p className="food-log-error">{error}</p>}
      <button
        type="button"
        onClick={handleLog}
        disabled={!name.trim() || !calories || logging}
        className="food-log-button"
      >
        {logging ? "Logging…" : "Log food"}
      </button>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────

export default function Dashboard({ nutritionData, userProfile, onOpenRecipeBuilder, onOpenSettings, onOpenSharedRecipes, onOpenBulkImport, onOpenMealPlanner }: DashboardProps) {
  const [meals, setMeals] = useState<Meal[]>([]);
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [selectedRecipe, setSelectedRecipe] = useState<{ [key in MealCategory]: string }>({
    breakfast: "",
    lunch: "",
    dinner: "",
    snacks: "",
  });
  const [selectedDate, setSelectedDate] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const isToday = selectedDate === new Date().toISOString().slice(0, 10);

  const [addTab, setAddTab] = useState<{ [key in MealCategory]: "food" | "custom" | "recipe" }>({
    breakfast: "food",
    lunch: "food",
    dinner: "food",
    snacks: "food",
  });

  // Measurements
  const [measurements, setMeasurements] = useState<BodyMeasurement[]>([]);
  const [measWeight, setMeasWeight]     = useState("");
  const [measHeight, setMeasHeight]     = useState("");
  const [measWaist, setMeasWaist]       = useState("");
  const [measNeck, setMeasNeck]         = useState("");
  const [measHip, setMeasHip]           = useState("");
  const [measDate, setMeasDate]         = useState(() => new Date().toISOString().slice(0, 10));
  const [measError, setMeasError]       = useState("");
  const [measSaving, setMeasSaving]     = useState(false);
  const chartRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    const fetchRecipes = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        setRecipes([]);
        return;
      }

      try {
        const res = await apiFetch("/recipes", {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          console.error("Failed to fetch recipes:", res.status);
          setRecipes([]);
          return;
        }
        const fetchedRecipes = await res.json();
        setRecipes(Array.isArray(fetchedRecipes) ? fetchedRecipes : []);
      } catch (error) {
        console.error("Failed to fetch recipes:", error);
        setRecipes([]);
      }
    };

    fetchRecipes();
  }, []);

  useEffect(() => {
    const fetchLogs = async () => {
      const token = localStorage.getItem("token");
      if (!token) { setMeals([]); return; }
      try {
        const res = await apiFetch(`/logs?log_date=${selectedDate}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) { setMeals([]); return; }
        const logs = await res.json();
        if (!Array.isArray(logs)) { setMeals([]); return; }
        setMeals(logs.map((log: any) => ({
          id:       log.id,
          name:     log.food_name,
          time:     new Date(log.logged_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
          category: (log.meal as MealCategory) || "snacks",
          calories: log.calories  || 0,
          protein:  log.protein_g || 0,
          carbs:    log.carbs_g   || 0,
          fat:      log.fat_g     || 0,
        })));
      } catch { setMeals([]); }
    };
    fetchLogs();
  }, [selectedDate]);

  useEffect(() => {
    const fetchMeasurements = async () => {
      const token = localStorage.getItem("token");
      if (!token) return;
      try {
        const res = await apiFetch("/measurements", {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) setMeasurements(await res.json());
      } catch { /* non-fatal */ }
    };
    fetchMeasurements();
  }, []);

  const handleLogMeasurement = async () => {
    setMeasError("");
    if (!measWeight && !measHeight && !measWaist && !measNeck && !measHip) {
      setMeasError("Enter at least one measurement.");
      return;
    }
    const token = localStorage.getItem("token");
    if (!token) return;
    setMeasSaving(true);
    try {
      const res = await apiFetch("/measurements", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          measured_at: measDate || undefined,
          weight_kg:   measWeight ? parseFloat(measWeight) : undefined,
          height_cm:   measHeight ? parseFloat(measHeight) : undefined,
          waist_cm:    measWaist  ? parseFloat(measWaist)  : undefined,
          neck_cm:     measNeck   ? parseFloat(measNeck)   : undefined,
          hip_cm:      measHip    ? parseFloat(measHip)    : undefined,
        }),
      });
      if (!res.ok) throw new Error();
      const saved: BodyMeasurement = await res.json();
      setMeasurements(prev => [...prev, saved].sort((a, b) =>
        a.measured_at.localeCompare(b.measured_at)
      ));
      setMeasWeight(""); setMeasHeight(""); setMeasWaist(""); setMeasNeck(""); setMeasHip("");
    } catch {
      setMeasError("Failed to save measurement.");
    } finally {
      setMeasSaving(false);
    }
  };

  const refreshLogs = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) return;
    try {
      const res = await apiFetch(`/logs?log_date=${selectedDate}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) return;
      const logs = await res.json();
      setMeals(logs.map((log: any) => ({
        id:       log.id,
        name:     log.food_name,
        time:     new Date(log.logged_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
        category: (log.meal as MealCategory) || "snacks",
        calories: log.calories  || 0,
        protein:  log.protein_g || 0,
        carbs:    log.carbs_g   || 0,
        fat:      log.fat_g     || 0,
      })));
    } catch { /* non-fatal */ }
  }, [selectedDate]);

  const handleAddRecipe = async (category: MealCategory) => {
    const recipeId = selectedRecipe[category];
    if (!recipeId) return;
    const recipe = recipes.find((r) => r.id === recipeId);
    if (!recipe) return;
    const token = localStorage.getItem("token");
    if (!token) return;
    try {
      const res = await apiFetch("/logs", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          recipe_id: recipeId,
          food_name: recipe.name,
          amount_g:  100,
          calories:  recipe.total_calories,
          protein_g: recipe.total_protein_g,
          carbs_g:   recipe.total_carbs_g,
          fat_g:     recipe.total_fat_g,
          meal:      category,
          log_date:  selectedDate,
        }),
      });
      if (res.ok) {
        await refreshLogs();
        setSelectedRecipe(prev => ({ ...prev, [category]: "" }));
      }
    } catch (error) {
      console.error("Failed to add recipe to meal:", error);
    }
  };

  const categoryConsumed = (category: MealCategory) =>
    meals
      .filter((meal) => meal.category === category)
      .reduce((sum, meal) => sum + meal.calories, 0);

  const totalGoal = nutritionData.calories.goal || 2000;
  const CATEGORY_OPTIONS = MEAL_PROPORTIONS.map(c => ({
    ...c,
    goal: Math.round(totalGoal * c.pct),
  }));

  const mealsByCategory = CATEGORY_OPTIONS.map((category) => ({
    ...category,
    meals: meals.filter((meal) => meal.category === category.key),
    consumed: categoryConsumed(category.key),
  }));

  const totalConsumed = meals.reduce(
    (acc, meal) => ({
      calories: acc.calories + meal.calories,
      protein: acc.protein + meal.protein,
      carbs: acc.carbs + meal.carbs,
      fat: acc.fat + meal.fat,
    }),
    { calories: 0, protein: 0, carbs: 0, fat: 0 }
  );

  const progressPercent = (macro: keyof NutritionData) => {
    const consumed = totalConsumed[macro];
    const goal = nutritionData[macro].goal;
    return Math.min((consumed / goal) * 100, 100);
  };

  // safe_mode hides calorie/macro numbers — a mascot reacting "you haven't
  // eaten enough" / "you ate too much" is exactly the kind of judgment-coded
  // signal that exists to prevent, so it stays "happy" here too rather than
  // just hiding the numbers next to it.
  const safeMode = !!userProfile?.safe_mode;
  const calorieRatio = totalConsumed.calories / (nutritionData.calories.goal || 1);
  const calorieMood: "hungry" | "full" | "happy" = safeMode
    ? "happy"
    : calorieRatio < 0.7
    ? "hungry"
    : calorieRatio > 1.15
    ? "full"
    : "happy";

  return (
    <div className="dashboard-root" style={{
      backgroundImage: `url(${process.env.PUBLIC_URL}/polka_dots.png)`,
      backgroundSize: '280px 280px',
      backgroundRepeat: 'repeat'
    }}>
      <div className="dashboard-container">
        {/* Header */}
        <header className="dashboard-header">
          <div>
            <h1 className="dashboard-title">Welcome back! 👋</h1>
            <p className="dashboard-subtitle">Track your nutrition journey</p>
          </div>
          <div className="dashboard-header-actions">
            <button type="button" className="secondary-button" onClick={onOpenRecipeBuilder}>
              Create recipe
            </button>
            <button type="button" className="secondary-button" onClick={onOpenSharedRecipes}>Shared recipes</button>
            <button type="button" className="secondary-button" onClick={onOpenMealPlanner}>Meal planner</button>
            {userProfile?.is_admin && (
              <button type="button" className="secondary-button" onClick={onOpenBulkImport}>Bulk Import</button>
            )}
            <button type="button" className="secondary-button" onClick={onOpenSettings}>
              Settings
            </button>
          </div>
        </header>

        {/* Date navigation */}
        <div className="date-nav">
          <button
            type="button"
            className="date-nav-btn"
            onClick={() => {
              const d = new Date(selectedDate);
              d.setDate(d.getDate() - 1);
              setSelectedDate(d.toISOString().slice(0, 10));
            }}
          >
            ←
          </button>
          <input
            type="date"
            className="date-nav-input"
            value={selectedDate}
            max={new Date().toISOString().slice(0, 10)}
            onChange={e => setSelectedDate(e.target.value)}
          />
          <button
            type="button"
            className="date-nav-btn"
            disabled={isToday}
            onClick={() => {
              const d = new Date(selectedDate);
              d.setDate(d.getDate() + 1);
              setSelectedDate(d.toISOString().slice(0, 10));
            }}
          >
            →
          </button>
          {!isToday && (
            <button
              type="button"
              className="date-nav-today"
              onClick={() => setSelectedDate(new Date().toISOString().slice(0, 10))}
            >
              today
            </button>
          )}
        </div>

        {/* Overall calorie progress */}
        <section className="overall-calorie-section">
          <h2 className="section-title">Calorie Progress</h2>
          <div className="overall-progress-card">
            <img src={MOOD_IMAGES[calorieMood]} alt="" className="calorie-mascot" />
            <div className="overall-progress-label">
              <span>Today</span>
              {!safeMode && <strong>{totalConsumed.calories} / {nutritionData.calories.goal} cal</strong>}
            </div>
            <div className="overall-progress-bar">
              <div
                className="overall-progress-fill"
                style={{ width: `${progressPercent('calories')}%` }}
              />
            </div>
          </div>
        </section>

        {/* Nutrition Overview */}
        <section className="nutrition-overview">
          <h2 className="section-title">Macro Progress</h2>
          <div className="nutrition-grid">
            <div className="nutrition-card">
              <div className="macro-header">
                <span className="macro-name">Calories</span>
                {!safeMode && (
                  <span className="macro-values">
                    {totalConsumed.calories} / {nutritionData.calories.goal}
                  </span>
                )}
              </div>
              <div className="progress-bar">
                <div
                  className="progress-fill calories"
                  style={{ width: `${progressPercent('calories')}%` }}
                />
              </div>
            </div>

            <div className="nutrition-card">
              <div className="macro-header">
                <span className="macro-name">Protein</span>
                {!safeMode && (
                  <span className="macro-values">
                    {totalConsumed.protein}g / {nutritionData.protein.goal}g
                  </span>
                )}
              </div>
              <div className="progress-bar">
                <div
                  className="progress-fill protein"
                  style={{ width: `${progressPercent('protein')}%` }}
                />
              </div>
            </div>

            <div className="nutrition-card">
              <div className="macro-header">
                <span className="macro-name">Carbs</span>
                {!safeMode && (
                  <span className="macro-values">
                    {totalConsumed.carbs}g / {nutritionData.carbs.goal}g
                  </span>
                )}
              </div>
              <div className="progress-bar">
                <div
                  className="progress-fill carbs"
                  style={{ width: `${progressPercent('carbs')}%` }}
                />
              </div>
            </div>

            <div className="nutrition-card">
              <div className="macro-header">
                <span className="macro-name">Fat</span>
                {!safeMode && (
                  <span className="macro-values">
                    {totalConsumed.fat}g / {nutritionData.fat.goal}g
                  </span>
                )}
              </div>
              <div className="progress-bar">
                <div
                  className="progress-fill fat"
                  style={{ width: `${progressPercent('fat')}%` }}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Body Statistics */}
        <section className="body-stats-section">
          <h2 className="section-title">Body Statistics</h2>
          {(() => {
            // Age: calculate from birthday if available, otherwise fall back to stored age
            let age: string = "N/A";
            if (userProfile?.birthday) {
              const dob   = new Date(userProfile.birthday);
              const today = new Date();
              let a = today.getFullYear() - dob.getFullYear();
              const m = today.getMonth() - dob.getMonth();
              if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) a--;
              age = String(a);
            } else if (userProfile?.age) {
              age = String(userProfile.age);
            }

            const lastWith = <K extends keyof BodyMeasurement>(key: K) =>
              [...measurements].reverse().find(m => m[key] != null)?.[key] ?? null;

            // Height: from latest measurement with height, or onboarding value
            const height = lastWith("height_cm") ?? userProfile?.height_cm ?? null;

            // Weight: from latest measurement with weight, or onboarding value
            const weight = lastWith("weight_kg") ?? userProfile?.weight_kg ?? null;

            // Body fat: from latest measurement with body_fat_pct, or onboarding value.
            // Measurement rows are stored pre-rounded (see main.py), but the
            // onboarding-value fallback isn't — round here so both paths display
            // the same way instead of occasionally showing raw float precision.
            const bfRaw = lastWith("body_fat_pct") ?? userProfile?.body_fat_pct ?? null;
            const bf = bfRaw != null ? Math.round(bfRaw * 10) / 10 : null;

            return (
              <div className="body-stats-grid">
                <div className="stat-card">
                  <span className="stat-label">Weight</span>
                  <span className="stat-value">{weight != null ? `${weight} kg` : "N/A"}</span>
                </div>
                <div className="stat-card">
                  <span className="stat-label">Height</span>
                  <span className="stat-value">{height != null ? `${height} cm` : "N/A"}</span>
                </div>
                <div className="stat-card">
                  <span className="stat-label">Age</span>
                  <span className="stat-value">{age}</span>
                </div>
                <div className="stat-card">
                  <span className="stat-label">Body Fat %</span>
                  <span className="stat-value">{bf != null ? `${bf}%` : "N/A"}</span>
                </div>
              </div>
            );
          })()}

          {/* Weight chart */}
          <div className="weight-timeline">
            <h3>Weight Timeline</h3>
            {(() => {
              const pts = measurements.filter(m => m.weight_kg != null);
              if (pts.length === 0) {
                return <p className="timeline-empty">No weight entries yet. Log one below.</p>;
              }
              const W = 560, H = 120, PAD = { t: 12, r: 16, b: 28, l: 44 };
              const weights = pts.map(p => p.weight_kg as number);
              const minW = Math.min(...weights), maxW = Math.max(...weights);
              const range = maxW - minW || 1;
              const xStep = pts.length > 1
                ? (W - PAD.l - PAD.r) / (pts.length - 1)
                : (W - PAD.l - PAD.r) / 2;
              const toX = (i: number) => PAD.l + (pts.length > 1 ? i * xStep : (W - PAD.l - PAD.r) / 2);
              const toY = (w: number) => PAD.t + (H - PAD.t - PAD.b) * (1 - (w - minW) / range);
              const polyline = pts.map((p, i) => `${toX(i)},${toY(p.weight_kg as number)}`).join(" ");
              const yTicks = [minW, (minW + maxW) / 2, maxW];
              return (
                <svg ref={chartRef} viewBox={`0 0 ${W} ${H}`} className="weight-chart-svg">
                  {/* Y grid + labels */}
                  {yTicks.map((v, i) => {
                    const y = toY(v);
                    return (
                      <g key={i}>
                        <line x1={PAD.l} y1={y} x2={W - PAD.r} y2={y} stroke="#eaf5f3" strokeWidth="1" />
                        <text x={PAD.l - 4} y={y + 4} textAnchor="end" fontSize="9" fill="#8a6060">
                          {v.toFixed(1)}
                        </text>
                      </g>
                    );
                  })}
                  {/* Line */}
                  <polyline points={polyline} fill="none" stroke="#badfdb" strokeWidth="2.5" strokeLinejoin="round" />
                  {/* Dots + x labels + body fat % */}
                  {pts.map((p, i) => {
                    const x = toX(i), y = toY(p.weight_kg as number);
                    const label = p.measured_at.slice(5); // MM-DD
                    return (
                      <g key={p.id}>
                        <circle cx={x} cy={y} r="4" fill="#ffbdbd" />
                        {p.body_fat_pct != null && (
                          <text x={x} y={y - 8} textAnchor="middle" fontSize="9" fill="#c98a8a">
                            {Math.round(p.body_fat_pct * 10) / 10}%
                          </text>
                        )}
                        <text x={x} y={H - PAD.b + 12} textAnchor="middle" fontSize="9" fill="#8a6060">
                          {label}
                        </text>
                      </g>
                    );
                  })}
                </svg>
              );
            })()}
          </div>

          {/* Log measurement form */}
          <div className="measurement-form">
            <h3>Log measurement</h3>
            <div className="measurement-row">
              <label className="measurement-field">
                <span>Date</span>
                <input type="date" value={measDate} onChange={e => setMeasDate(e.target.value)} />
              </label>
              <label className="measurement-field">
                <span>Weight (kg)</span>
                <input type="number" min="0" step="0.1" placeholder="e.g. 72.5"
                  value={measWeight} onChange={e => setMeasWeight(e.target.value)} />
              </label>
              <label className="measurement-field">
                <span>Height (cm)</span>
                <input type="number" min="0" step="0.1" placeholder={userProfile?.height_cm ? `${userProfile.height_cm} (onboarding)` : "e.g. 168"}
                  value={measHeight} onChange={e => setMeasHeight(e.target.value)} />
              </label>
              <label className="measurement-field">
                <span>Waist (cm)</span>
                <input type="number" min="0" step="0.1" placeholder="e.g. 80"
                  value={measWaist} onChange={e => setMeasWaist(e.target.value)} />
              </label>
              <label className="measurement-field">
                <span>Neck (cm)</span>
                <input type="number" min="0" step="0.1" placeholder="e.g. 38"
                  value={measNeck} onChange={e => setMeasNeck(e.target.value)} />
              </label>
              <label className="measurement-field">
                <span>Hip (cm)</span>
                <input type="number" min="0" step="0.1" placeholder="e.g. 95"
                  value={measHip} onChange={e => setMeasHip(e.target.value)} />
              </label>
            </div>
            {measError && <p className="measurement-error">{measError}</p>}
            <button type="button" className="measurement-save-button"
              onClick={handleLogMeasurement} disabled={measSaving}>
              {measSaving ? "Saving…" : "Save measurement"}
            </button>
          </div>
        </section>

        <section className="category-section">
          <h2 className="section-title">Meals by Category</h2>
          <div className="category-grid">
            {mealsByCategory.map((category) => (
              <div key={category.key} className="category-card">
                <div className="category-header">
                  <div>
                    <h3>{category.label}</h3>
                    <p>{category.consumed} / {category.goal} cal</p>
                  </div>
                  <div className="category-progress-bar">
                    <div
                      className="category-progress-fill"
                      style={{ width: `${Math.min((category.consumed / category.goal) * 100, 100)}%` }}
                    />
                  </div>
                </div>
                {category.meals.length === 0 ? (
                  <div className="category-empty">No {category.label.toLowerCase()} logged yet.</div>
                ) : (
                  <div className="category-meals">
                    {category.meals.map((meal) => (
                      <div key={meal.id} className="meal-card">
                        <div className="meal-header">
                          <h3 className="meal-name">{meal.name}</h3>
                          <span className="meal-time">{meal.time}</span>
                        </div>
                        <div className="meal-macros">
                          <span className="macro-item">{meal.calories} cal</span>
                          <span className="macro-item">{meal.protein}g P</span>
                          <span className="macro-item">{meal.carbs}g C</span>
                          <span className="macro-item">{meal.fat}g F</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="add-food-form">
                  <div className="add-food-tabs">
                    <button
                      type="button"
                      className={`add-tab-btn${addTab[category.key] === "food" ? " active" : ""}`}
                      onClick={() => setAddTab(prev => ({ ...prev, [category.key]: "food" }))}
                    >
                      Food
                    </button>
                    <button
                      type="button"
                      className={`add-tab-btn${addTab[category.key] === "custom" ? " active" : ""}`}
                      onClick={() => setAddTab(prev => ({ ...prev, [category.key]: "custom" }))}
                    >
                      Custom
                    </button>
                    <button
                      type="button"
                      className={`add-tab-btn${addTab[category.key] === "recipe" ? " active" : ""}`}
                      onClick={() => setAddTab(prev => ({ ...prev, [category.key]: "recipe" }))}
                    >
                      Recipe
                    </button>
                  </div>

                  {addTab[category.key] === "food" ? (
                    <FoodLogInput category={category.key} logDate={selectedDate} onLogged={refreshLogs} />
                  ) : addTab[category.key] === "custom" ? (
                    <CustomFoodInput category={category.key} logDate={selectedDate} onLogged={refreshLogs} />
                  ) : (
                    <div className="recipe-log-form">
                      <select
                        value={selectedRecipe[category.key]}
                        onChange={(e) => setSelectedRecipe(prev => ({ ...prev, [category.key]: e.target.value }))}
                      >
                        <option value="">Select a recipe…</option>
                        {recipes.map((recipe) => (
                          <option key={recipe.id} value={recipe.id}>
                            {recipe.name} ({recipe.total_calories} cal)
                          </option>
                        ))}
                      </select>
                      <button type="button" onClick={() => handleAddRecipe(category.key)}>
                        Add Recipe
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}